import React, { useId } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Collapsible group on the merchant Settings tab (FIG-320 / REQ-022): starts
// collapsed to its title and a one-line summary; the title is a button, so
// click or Enter toggles it. Open state is owned by the tab (Expand/Collapse all).

interface SettingsGroupProps {
  id: string;
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const SettingsGroup: React.FC<SettingsGroupProps> = ({ id, title, summary, open, onToggle, children }) => {
  const bodyId = useId();
  const buttonId = `settings-group-${id}`;
  return (
    <section aria-labelledby={buttonId} className="rounded-lg border bg-white shadow-sm" data-testid={buttonId}>
      <h2 className="m-0">
        <button
          type="button"
          id={buttonId}
          aria-expanded={open}
          // The body only exists while open, so it is only referenced then.
          aria-controls={open ? bodyId : undefined}
          onClick={onToggle}
          className="flex w-full items-center gap-3 rounded-lg px-5 py-4 text-left hover:bg-muted/40"
        >
          <ChevronRight
            aria-hidden="true"
            className={cn('h-4 w-4 flex-none text-muted-foreground transition-transform', open && 'rotate-90')}
          />
          <span className="flex min-w-0 flex-col">
            <span className="text-lg font-semibold">{title}</span>
            <span className="text-sm font-normal text-muted-foreground">{summary}</span>
          </span>
        </button>
      </h2>
      {open && (
        <div id={bodyId} className="space-y-4 px-4 pb-4">
          {children}
        </div>
      )}
    </section>
  );
};

/** A titled card inside a group. */
export const SettingsSection: React.FC<{
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Anchor id, e.g. for a "Change in …" link from another section. */
  id?: string;
}> = ({ title, description, action, children, className, id }) => (
  <section id={id} className={cn('rounded-md border p-5', className)} aria-label={title}>
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

/** Settings-row layout (REQ-025): muted label, value, right-aligned edit control. */
export const SettingsRow: React.FC<{
  label: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  editing?: boolean;
  testId?: string;
}> = ({ label, children, action, editing, testId }) => (
  <div
    data-testid={testId}
    className={cn(
      'grid grid-cols-[minmax(140px,200px)_1fr_auto] items-start gap-4 border-t px-1 py-4 first:border-t-0',
      editing && 'rounded-md bg-muted/40',
    )}
  >
    <div className="pt-1 text-sm text-muted-foreground">{label}</div>
    <div className="min-w-0">{children}</div>
    <div className="flex justify-end">{action}</div>
  </div>
);
